/**
 * Marking a whole paragraph as one tracked insertion or one tracked deletion.
 *
 * Word records a paragraph-level change in two places at once: on the
 * paragraph mark, as `w:pPr/w:rPr/w:ins` or `w:del`, and on the content, as a
 * `w:ins` or `w:del` wrapper around each run. A consumer that reads only one
 * of the two keeps the paragraph's text on Reject or on Accept, so both are
 * always written together. `OoxmlRange.insertParagraph` writes the insertion
 * shape for a paragraph added to the body; the table row cloner writes it for
 * every paragraph of a new row, and the deletion shape for every paragraph of
 * a row being removed.
 */
import { DELETION_CONTAINERS, WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "./ooxml-names";
import type { Revision } from "./revision";

/** Paragraph children Word lets a `w:ins` or `w:del` wrap directly; property blocks and markers stay outside. */
const WRAPPABLE_PARAGRAPH_CONTENT = new Set(["r", "hyperlink"]);

/** `w:t` becomes `w:delText` and `w:instrText` becomes `w:delInstrText` inside a deletion. */
const DELETED_TEXT_ELEMENT_NAMES = { t: "w:delText", instrText: "w:delInstrText" } as const satisfies Record<string, string>;

export class ParagraphRevisionMarks {
    /**
     * Marks `paragraph` as inserted by `revision`: the paragraph-mark record
     * under `w:pPr/w:rPr`, then a stamped `w:ins` around every run and
     * hyperlink that is a direct child.
     *
     * WHEN the paragraph mark already carries a `w:ins`
     *   CONSEQUENCE
     *     Nothing is added: a second record would duplicate the signal.
     */
    static markInserted(paragraph: Element, revision: Revision): void {
        const paragraphMarkRunProperties = ParagraphRevisionMarks.paragraphMarkRunProperties(paragraph);
        if (ParagraphRevisionMarks.childNamed(paragraphMarkRunProperties, "ins") !== null) return;
        const ownerDocument = paragraph.ownerDocument as Document;
        paragraphMarkRunProperties.insertBefore(revision.stamp(ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:ins")), paragraphMarkRunProperties.firstChild);
        for (const child of Array.from(paragraph.childNodes)) {
            if (child.nodeType !== 1 || !WRAPPABLE_PARAGRAPH_CONTENT.has(elementNameWithoutPrefix(child as Element))) continue;
            const insertionWrapper = revision.stamp(ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:ins"));
            paragraph.replaceChild(insertionWrapper, child);
            insertionWrapper.appendChild(child);
        }
    }

    /**
     * Marks `paragraph` as deleted by `revision`: the paragraph-mark record
     * under `w:pPr/w:rPr`, then a stamped `w:del` around every run not already
     * inside a deletion, wherever it sits (a hyperlink, a content control,
     * another author's `w:ins`), with its text elements renamed to Word's
     * deleted-text spellings.
     *
     * WHEN a run is already inside `w:del` or `w:moveFrom`
     *   CONSEQUENCE
     *     Left as it is: it is struck already and a second wrapper would be a deletion of a deletion.
     *
     * WHEN the paragraph's mark ends a section (`holdsSectionBreak`)
     *   CONSEQUENCE
     *     its runs are struck and its mark kept (`markParagraphMarkDeleted`): once
     *     accepted an empty paragraph stays and carries the section break.
     */
    static markDeleted(paragraph: Element, revision: Revision): void {
        ParagraphRevisionMarks.markParagraphMarkDeleted(paragraph, revision);
        for (const run of Array.from(paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, "r"))) {
            if (ParagraphRevisionMarks.isInsidePropertyBlockOrDeletion(run, paragraph)) continue;
            ParagraphRevisionMarks.strikeRun(run, revision);
        }
    }

    /**
     * Strikes one run as `revision`'s deletion: its text elements renamed to
     * Word's deleted-text spellings (`w:t` -> `w:delText`, `w:instrText` ->
     * `w:delInstrText`) and a stamped `w:del` put around it where it stands. A
     * field-marker run is struck the same way, which is how Word deletes a whole
     * field with tracking on.
     *
     * The caller leaves out a run already inside a deletion: a second wrapper
     * would be a deletion of a deletion.
     */
    static strikeRun(run: Element, revision: Revision): void {
        const ownerDocument = run.ownerDocument as Document;
        for (const textElement of Array.from(run.getElementsByTagNameNS(WORDPROCESSINGML_NS, "*"))) {
            const textElementName = elementNameWithoutPrefix(textElement);
            if (!Object.hasOwn(DELETED_TEXT_ELEMENT_NAMES, textElementName)) continue;
            const renamedTextElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, DELETED_TEXT_ELEMENT_NAMES[textElementName as keyof typeof DELETED_TEXT_ELEMENT_NAMES]);
            for (const attribute of Array.from(textElement.attributes)) renamedTextElement.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
            while (textElement.firstChild !== null) renamedTextElement.appendChild(textElement.firstChild);
            textElement.parentNode?.replaceChild(renamedTextElement, textElement);
        }
        const deletionWrapper = revision.stamp(ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:del"));
        run.parentNode?.replaceChild(deletionWrapper, run);
        deletionWrapper.appendChild(run);
    }

    /**
     * WHEN a `w:r` sits under `w:rPr` (a paragraph mark's run properties), or under `w:del` / `w:moveFrom`
     *   CONSEQUENCE
     *     It is not content to strike: the first is a property block, the others are struck already.
     */
    private static isInsidePropertyBlockOrDeletion(run: Element, paragraph: Element): boolean {
        for (let ancestor = run.parentNode; ancestor !== null && ancestor !== paragraph && ancestor.nodeType === 1; ancestor = ancestor.parentNode) {
            const ancestorName = elementNameWithoutPrefix(ancestor as Element);
            if (ancestorName === "rPr" || DELETION_CONTAINERS.has(ancestorName)) return true;
        }
        return false;
    }

    /**
     * Marks only the paragraph's mark deleted by `revision` (`w:pPr/w:rPr/w:del`), its
     * runs left as they are: what the builder writes once the engine has struck every
     * word of a paragraph the card deletes whole.
     *
     * WHEN the paragraph's mark ends a section (`holdsSectionBreak`)
     *   CONSEQUENCE
     *     left as it is: Word removes a deleted mark on Accept and the section's
     *     properties go with it (`markDeleted`).
     *
     * WHEN the mark already carries a deletion record
     *   CONSEQUENCE
     *     left as it is: a second record would duplicate the signal.
     */
    static markParagraphMarkDeleted(paragraph: Element, revision: Revision): void {
        if (ParagraphRevisionMarks.holdsSectionBreak(paragraph)) return;
        const paragraphMarkRunProperties = ParagraphRevisionMarks.paragraphMarkRunProperties(paragraph);
        if (ParagraphRevisionMarks.childNamed(paragraphMarkRunProperties, "del") !== null) return;
        paragraphMarkRunProperties.insertBefore(revision.stamp((paragraph.ownerDocument as Document).createElementNS(WORDPROCESSINGML_NS, "w:del")), paragraphMarkRunProperties.firstChild);
    }

    /**
     * Whether the paragraph's mark ends a section: its `w:pPr` holds a `w:sectPr`, the
     * page size, orientation, margins, headers and numbering of the section before it.
     *
     * WHY it matters to a deletion
     *   Accepting a deleted paragraph mark removes the mark, and a section break's
     *   properties live on it: the section merges into the next, its page setup and
     *   headers lost. A section-break paragraph is struck run by run and keeps its mark
     *   (R34 review, -42, 2026-10-16: a page-break paragraph that ended a section).
     */
    static holdsSectionBreak(paragraph: Element): boolean {
        const paragraphProperties = ParagraphRevisionMarks.childNamed(paragraph, "pPr");
        return paragraphProperties !== null && ParagraphRevisionMarks.childNamed(paragraphProperties, "sectPr") !== null;
    }

    /**
     * `w:pPr/w:rPr` of the paragraph, creating both when missing; the paragraph-mark
     * revision record lives there.
     *
     * WHY a new `w:rPr` goes before `w:sectPr` and `w:pPrChange`
     *   CT_PPr orders its children, and `w:rPr` comes before `w:sectPr` and
     *   `w:pPrChange`, its last two. Appended after them, the paragraph broke the
     *   schema Word reads a package against (R34 review, -42, 2026-10-16).
     *
     * EXAMPLE
     *   <w:pPr><w:pStyle/><w:sectPr/></w:pPr>   -> <w:pPr><w:pStyle/><w:rPr/><w:sectPr/></w:pPr>
     *   <w:pPr><w:pStyle/></w:pPr>              -> <w:pPr><w:pStyle/><w:rPr/></w:pPr>
     */
    private static paragraphMarkRunProperties(paragraph: Element): Element {
        const ownerDocument = paragraph.ownerDocument as Document;
        let paragraphProperties = ParagraphRevisionMarks.childNamed(paragraph, "pPr");
        if (paragraphProperties === null) {
            paragraphProperties = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:pPr");
            paragraph.insertBefore(paragraphProperties, paragraph.firstChild);
        }
        let paragraphMarkRunProperties = ParagraphRevisionMarks.childNamed(paragraphProperties, "rPr");
        if (paragraphMarkRunProperties === null) {
            paragraphMarkRunProperties = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:rPr");
            /** The first of the children CT_PPr puts after `w:rPr`; null when there is none, and the new `w:rPr` goes last. */
            const childAfterRunProperties = ParagraphRevisionMarks.childNamed(paragraphProperties, "sectPr") ?? ParagraphRevisionMarks.childNamed(paragraphProperties, "pPrChange");
            paragraphProperties.insertBefore(paragraphMarkRunProperties, childAfterRunProperties);
        }
        return paragraphMarkRunProperties;
    }

    /**
     * Whether the paragraph itself is struck: its mark carries `w:pPr/w:rPr/w:del`,
     * the record Word reads to remove the paragraph on Accept. Reads only; a
     * paragraph without `w:pPr` or `w:rPr` is left as it is.
     */
    static isMarkDeleted(paragraph: Element): boolean {
        const paragraphProperties = ParagraphRevisionMarks.childNamed(paragraph, "pPr");
        if (paragraphProperties === null) return false;
        const paragraphMarkRunProperties = ParagraphRevisionMarks.childNamed(paragraphProperties, "rPr");
        if (paragraphMarkRunProperties === null) return false;
        return ParagraphRevisionMarks.childNamed(paragraphMarkRunProperties, "del") !== null;
    }

    private static childNamed(parent: Element, localName: string): Element | null {
        for (const child of Array.from(parent.childNodes)) {
            if (child.nodeType === 1 && elementNameWithoutPrefix(child as Element) === localName) return child as Element;
        }
        return null;
    }
}
